import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_fdfa1102-6a78-496f-adc3-9918563c18d9/artifacts/a4oqwkvk_Propac%20logo.png";

export default function ChangePassword() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState("");

    const forced = !!user?.must_change_password;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        if (newPassword.length < 4) {
            setError("New password must be at least 4 characters.");
            return;
        }
        if (newPassword !== confirm) {
            setError("New password and confirmation do not match.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: currentPassword,
                new_password: newPassword,
            });
            await refresh();
            setSuccess("Password updated.");
            setTimeout(() => navigate("/dashboard"), 500);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--odr-bg)] px-4">
            <div
                className="w-full max-w-md bg-white border border-gray-200 rounded-sm"
                data-testid="change-password-card"
            >
                <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-5">
                    <img
                        src={LOGO_URL}
                        alt="Pro-Pac"
                        className="w-10 h-10 object-contain"
                    />
                    <div>
                        <div className="text-lg font-semibold text-gray-900">
                            {forced ? "Set Your Password" : "Change Password"}
                        </div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">
                            {forced
                                ? "First-time login requires a new password"
                                : `Signed in as ${user?.username}`}
                        </div>
                    </div>
                </div>
                <form className="px-6 py-6 space-y-4" onSubmit={onSubmit}>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                            Current Password
                        </label>
                        <input
                            type="password"
                            autoFocus
                            data-testid="cp-current"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                            New Password
                        </label>
                        <input
                            type="password"
                            data-testid="cp-new"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                            Confirm New Password
                        </label>
                        <input
                            type="password"
                            data-testid="cp-confirm"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                        />
                    </div>
                    {error && (
                        <div
                            className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2"
                            data-testid="cp-error"
                        >
                            {error}
                        </div>
                    )}
                    {success && (
                        <div
                            className="text-sm border border-green-200 bg-green-50 text-green-800 rounded-sm px-3 py-2"
                            data-testid="cp-success"
                        >
                            {success}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="cp-submit"
                            className="odr-btn flex-1 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold uppercase tracking-wider transition-colors disabled:opacity-60"
                        >
                            {submitting ? "Updating…" : "Update Password"}
                        </button>
                        {forced && (
                            <button
                                type="button"
                                onClick={logout}
                                data-testid="cp-logout"
                                className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Sign out
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
